import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      dashboard: 'Dashboard',
      orders: 'Orders',
      kitchen: 'Kitchen',
      customers: 'Customers',
      menu: 'Menu',
      whatsapp: 'WhatsApp',
      reports: 'Reports',
      settings: 'Settings',
      companies: 'Companies',
      restaurants: 'Restaurants',
      users: 'Users',
      logout: 'Logout',
      ordersToday: 'Orders Today',
      revenueToday: 'Revenue Today',
      activeCustomers: 'Active Customers',
      averageOrderValue: 'Average Order Value',
      sales: 'Sales',
      salesToday: 'Sales Today',
      salesYesterday: 'Sales Yesterday',
      salesLast7Days: 'Sales (Last 7 Days)',
      salesLast30Days: 'Sales (Last 30 Days)',
      salesThisMonth: 'Sales This Month',
      ordersYesterday: 'Orders Yesterday',
      ordersLast7Days: 'Orders (Last 7 Days)',
      ordersLast30Days: 'Orders (Last 30 Days)',
      ordersThisMonth: 'Orders This Month',
      welcomeBack: 'Welcome back!',
      restaurantPerformanceLine: "Here's how your restaurant is performing.",
      salesAnalytics: 'Sales Analytics',
      orderStatusAnalytics: 'Order Status Analytics',
      newOrders: 'New Orders',
      preparing: 'Preparing',
      ready: 'Ready',
      completed: 'Completed',
      searchOrders: 'Search orders...',
      filterByStatus: 'Filter by status',
      orderNumber: 'Order Number',
      customer: 'Customer',
      total: 'Total',
      status: 'Status',
      actions: 'Actions',
      viewDetails: 'View Details',
      addCustomer: 'Add Customer',
      customerName: 'Customer Name',
      phone: 'Phone',
      email: 'Email',
      totalOrders: 'Total Orders',
      totalSpent: 'Total Spent',
      addMenuItem: 'Add Menu Item',
      itemName: 'Item Name',
      price: 'Price',
      category: 'Category',
      available: 'Available',
      unavailable: 'Unavailable',
      dailySales: 'Daily Sales',
      weeklySales: 'Weekly Sales',
      monthlySales: 'Monthly Sales',
      topSellingItems: 'Top Selling Items',
      login: 'Login',
      register: 'Register',
      password: 'Password',
      name: 'Name',
      welcome: 'Welcome to UniGate Solution',
      restaurantOnboarding: {
        title: 'Restaurant onboarding',
        loading: 'Loading your onboarding session...',
        sessionUnavailable: 'Onboarding session unavailable',
        staleSession: 'The saved session is no longer available. Recover your setup using the existing request reference.',
        staleMissingRequest: 'The previous setup cannot be resumed automatically because its recovery reference is missing. Contact support before starting again.',
        recover: 'Recover setup',
        retry: 'Retry',
        authenticationExpired: 'Your session has expired. Please sign in again.',
        permissionDenied: 'You do not have permission to onboard a restaurant.',
        validationError: 'The onboarding request could not be validated.',
        temporaryError: 'The onboarding service is temporarily unavailable. Please try again.',
        genericError: 'Unable to load restaurant onboarding. Please try again.',
        completedTitle: 'Setup completed',
        completed: 'This restaurant setup has already been completed.',
        sessionReady: 'Setup session ready',
        foundationReady: 'Your secure onboarding session is ready. Complete the restaurant details below, save the draft, and review the saved information before creating the restaurant.',
        companyConfirmation: 'Company confirmation',
        company: 'Company',
        subscriptionPlan: 'Subscription plan: {{plan}}',
        restaurantDetails: 'Restaurant details',
        draftHelp: 'Save a recoverable draft. This does not complete onboarding.',
        restaurantName: 'Restaurant name',
        address: 'Address',
        phone: 'Phone',
        whatsappNumber: 'WhatsApp number',
        whatsappReadiness: 'WhatsApp readiness',
        whatsappNumberMissing: 'No WhatsApp number has been saved yet.',
        whatsappNumberSaved: 'WhatsApp number saved',
        whatsappConfigurationPending: 'WhatsApp configuration is required after restaurant creation.',
        whatsappNoProvisioning: 'Saving a phone number does not connect WhatsApp. No provisioning is performed during this step.',
        currency: 'Currency (3-letter code)',
        taxEnabled: 'Enable tax',
        taxRate: 'Tax rate (%)',
        saveDraft: 'Save draft',
        saving: 'Saving...',
        saved: 'Draft saved.',
        saveNotReady: 'Save draft is not available right now. Refresh the page and try again.',
        revisionConflict: 'This session changed elsewhere. The latest version has been loaded; review it before saving again.',
        revisionConflictTitle: 'Another change was detected',
        latestVersionLoaded: 'The latest server version has been loaded.',
        previousValuesPreserved: 'Your previous values were preserved here for optional manual reapplication.',
        restorePreviousValues: 'Restore my previous values',
        dismissConflict: 'Dismiss and review latest version',
        nameTooLong: 'Restaurant name must be 160 characters or fewer.',
        addressTooLong: 'Address must be 500 characters or fewer.',
        phoneTooLong: 'Phone numbers must be 40 characters or fewer.',
        currencyInvalid: 'Currency must be a three-letter code such as USD.',
        taxRateInvalid: 'Tax rate must be between 0 and 100.',
        taxDisabledRate: 'Tax rate must be zero when tax is disabled.',
        reviewTitle: 'Review restaurant setup',
        reviewHelp: 'Review the saved restaurant details before creating the restaurant.',
        notProvided: 'Not provided',
        yes: 'Yes',
        no: 'No',
        completionNoticeTitle: 'Before you create the restaurant',
        completionNotice: 'Creating the restaurant completes this onboarding session. Review the saved details carefully before continuing.',
        unsavedChangesBeforeCreate: 'Save your changes before creating the restaurant.',
        savedDraftNotReady: 'Save a valid restaurant name and currency before creating the restaurant.',
        createRestaurant: 'Create restaurant',
        creatingRestaurant: 'Creating restaurant...',
        completionNotReady: 'The restaurant setup is not ready to be completed. Review the saved details and try again.',
        completionTemporaryError: 'Restaurant creation is temporarily unavailable. Check the current completion status before trying again.',
        completionGenericError: 'Restaurant creation could not be completed. Check the current status and try again if allowed.',
        completionPendingTitle: 'Restaurant creation in progress',
        completionPending: 'A restaurant creation attempt is still in progress. Check the status before taking another action.',
        checkStatus: 'Check status',
        completionRetryableTitle: 'Restaurant creation can be retried',
        completionRetryable: 'The previous completion attempt did not finish successfully. The server has confirmed that it is safe to retry.',
        retryCompletion: 'Retry restaurant creation',
        completionBlockedTitle: 'Restaurant creation needs support',
        completionBlocked: 'The restaurant cannot be safely retried from this screen. Check the status or contact support.',
        completionReference: 'Completion reference: {{code}}',
        completedSummaryTitle: 'Restaurant created successfully',
        completedSummary: 'The restaurant has been created and this onboarding session is complete.',
        managerAssignmentDeferred: 'Restaurant manager assignment will be completed in the next onboarding stage.',
        assignRestaurantManager: 'Assign restaurant manager',
        selectManager: 'Select manager',
        assignManager: 'Assign manager',
        assigningManager: 'Assigning manager...',
        assignLater: 'Assign later',
        assigned: 'Assigned',
        assignedToThisRestaurant: 'Assigned to this restaurant',
        assignedToAnotherRestaurant: 'Assigned to another restaurant',
        noManagersAvailable: 'No restaurant managers are available yet.',
        assignManagerLater: 'You can assign a manager later.',
        managerAssignmentSuccess: 'Manager assignment successful.',
        managerAssignmentChanged: 'Manager assignment changed. Reload and choose again.',
        managerAssignmentUnavailable: 'Manager assignment is temporarily unavailable. Please try again.',
        managerAssignmentUnable: 'Unable to assign manager. Review the latest manager status and try again.',
        managerAssignmentAuthorizationError: 'Your session does not allow manager assignment. Please sign in again or contact an administrator.',
        managerAssignmentValidationError: 'The manager assignment could not be validated.',
        managerAssignmentLoadError: 'Unable to load restaurant managers. Please try again.',
        managerAssignmentMissingRestaurant: 'Unable to assign a manager because the completed restaurant identity is unavailable.',
        loadingManagers: 'Loading managers...',
        whatsappReviewSaved: 'WhatsApp number saved: {{number}}',
        whatsappReviewNotConnected: 'This number is saved for the restaurant, but WhatsApp is not connected or provisioned yet.',
        whatsappReviewMissing: 'No WhatsApp number is saved. WhatsApp can be configured after restaurant creation.'
      }
    }
  },

  ar: {
    translation: {
      dashboard: 'لوحة التحكم',
      orders: 'الطلبات',
      kitchen: 'المطبخ',
      customers: 'العملاء',
      menu: 'القائمة',
      whatsapp: 'واتساب',
      reports: 'التقارير',
      settings: 'الإعدادات',
      companies: 'الشركات',
      restaurants: 'المطاعم',
      users: 'المستخدمون',
      logout: 'تسجيل الخروج',
      ordersToday: 'طلبات اليوم',
      revenueToday: 'إيرادات اليوم',
      activeCustomers: 'العملاء النشطون',
      averageOrderValue: 'متوسط قيمة الطلب',
      sales: 'المبيعات',
      salesToday: 'مبيعات اليوم',
      salesYesterday: 'مبيعات أمس',
      salesLast7Days: 'المبيعات (آخر 7 أيام)',
      salesLast30Days: 'المبيعات (آخر 30 يوماً)',
      salesThisMonth: 'مبيعات هذا الشهر',
      ordersYesterday: 'طلبات أمس',
      ordersLast7Days: 'الطلبات (آخر 7 أيام)',
      ordersLast30Days: 'الطلبات (آخر 30 يوماً)',
      ordersThisMonth: 'طلبات هذا الشهر',
      welcomeBack: 'مرحباً بعودتك!',
      restaurantPerformanceLine: 'إليك أداء مطعمك حالياً.',
      salesAnalytics: 'تحليلات المبيعات',
      orderStatusAnalytics: 'تحليلات حالة الطلب',
      newOrders: 'طلبات جديدة',
      preparing: 'قيد التحضير',
      ready: 'جاهز',
      completed: 'مكتمل',
      searchOrders: 'بحث الطلبات...',
      filterByStatus: 'تصفية حسب الحالة',
      orderNumber: 'رقم الطلب',
      customer: 'العميل',
      total: 'المجموع',
      status: 'الحالة',
      actions: 'الإجراءات',
      viewDetails: 'عرض التفاصيل',
      addCustomer: 'إضافة عميل',
      customerName: 'اسم العميل',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      totalOrders: 'إجمالي الطلبات',
      totalSpent: 'إجمالي الإنفاق',
      addMenuItem: 'إضافة عنصر قائمة',
      itemName: 'اسم العنصر',
      price: 'السعر',
      category: 'الفئة',
      available: 'متاح',
      unavailable: 'غير متاح',
      dailySales: 'المبيعات اليومية',
      weeklySales: 'المبيعات الأسبوعية',
      monthlySales: 'المبيعات الشهرية',
      topSellingItems: 'العناصر الأكثر مبيعاً',
      login: 'تسجيل الدخول',
      register: 'التسجيل',
      password: 'كلمة المرور',
      name: 'الاسم',
      welcome: 'مرحباً بك في يونيجيت سوليوشن',
      restaurantOnboarding: {
        title: 'إعداد المطعم',
        loading: 'جارٍ تحميل جلسة إعداد المطعم...',
        sessionUnavailable: 'جلسة الإعداد غير متاحة',
        staleSession: 'لم تعد الجلسة المحفوظة متاحة. يمكنك استعادة الإعداد باستخدام مرجع الطلب الحالي.',
        staleMissingRequest: 'لا يمكن استعادة الإعداد السابق تلقائياً لأن مرجع الاستعادة مفقود. تواصل مع الدعم قبل البدء من جديد.',
        recover: 'استعادة الإعداد',
        retry: 'إعادة المحاولة',
        authenticationExpired: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
        permissionDenied: 'ليست لديك صلاحية إعداد مطعم.',
        validationError: 'تعذر التحقق من طلب الإعداد.',
        temporaryError: 'خدمة الإعداد غير متاحة مؤقتاً. يرجى المحاولة مرة أخرى.',
        genericError: 'تعذر تحميل إعداد المطعم. يرجى المحاولة مرة أخرى.',
        completedTitle: 'اكتمل الإعداد',
        completed: 'تم إكمال إعداد هذا المطعم مسبقاً.',
        sessionReady: 'جلسة الإعداد جاهزة',
        foundationReady: 'جلسة الإعداد الآمنة جاهزة. أكمل بيانات المطعم أدناه، واحفظ المسودة، ثم راجع البيانات المحفوظة قبل إنشاء المطعم.',
        companyConfirmation: 'تأكيد الشركة',
        company: 'الشركة',
        subscriptionPlan: 'خطة الاشتراك: {{plan}}',
        restaurantDetails: 'بيانات المطعم',
        draftHelp: 'احفظ مسودة قابلة للاستعادة. هذا لا يكمل عملية الإعداد.',
        restaurantName: 'اسم المطعم',
        address: 'العنوان',
        phone: 'الهاتف',
        whatsappNumber: 'رقم واتساب',
        whatsappReadiness: 'جاهزية واتساب',
        whatsappNumberMissing: 'لم يتم حفظ رقم واتساب بعد.',
        whatsappNumberSaved: 'تم حفظ رقم واتساب',
        whatsappConfigurationPending: 'يلزم إعداد واتساب بعد إنشاء المطعم.',
        whatsappNoProvisioning: 'حفظ رقم الهاتف لا يربط واتساب. لا يتم إجراء أي تهيئة في هذه الخطوة.',
        currency: 'العملة (رمز من 3 أحرف)',
        taxEnabled: 'تفعيل الضريبة',
        taxRate: 'نسبة الضريبة (%)',
        saveDraft: 'حفظ المسودة',
        saving: 'جارٍ الحفظ...',
        saved: 'تم حفظ المسودة.',
        saveNotReady: 'حفظ المسودة غير متاح حاليًا. حدّث الصفحة وحاول مرة أخرى.',
        revisionConflict: 'تغيرت هذه الجلسة في مكان آخر. تم تحميل أحدث نسخة؛ راجعها قبل الحفظ مجددًا.',
        revisionConflictTitle: 'تم اكتشاف تغيير آخر',
        latestVersionLoaded: 'تم تحميل أحدث نسخة من الخادم.',
        previousValuesPreserved: 'تم الاحتفاظ بالقيم السابقة هنا لإعادة تطبيقها يدوياً عند الحاجة.',
        restorePreviousValues: 'استعادة القيم السابقة',
        dismissConflict: 'إغلاق ومراجعة أحدث نسخة',
        nameTooLong: 'يجب ألا يتجاوز اسم المطعم 160 حرفًا.',
        addressTooLong: 'يجب ألا يتجاوز العنوان 500 حرف.',
        phoneTooLong: 'يجب ألا تتجاوز أرقام الهاتف 40 حرفًا.',
        currencyInvalid: 'يجب أن تكون العملة رمزًا من ثلاثة أحرف مثل USD.',
        taxRateInvalid: 'يجب أن تكون نسبة الضريبة بين 0 و100.',
        taxDisabledRate: 'يجب أن تكون نسبة الضريبة صفرًا عند تعطيلها.',
        reviewTitle: 'مراجعة إعداد المطعم',
        reviewHelp: 'راجع بيانات المطعم المحفوظة قبل إنشاء المطعم.',
        notProvided: 'غير مدخل',
        yes: 'نعم',
        no: 'لا',
        completionNoticeTitle: 'قبل إنشاء المطعم',
        completionNotice: 'إنشاء المطعم سيكمل جلسة الإعداد الحالية. راجع البيانات المحفوظة بعناية قبل المتابعة.',
        unsavedChangesBeforeCreate: 'احفظ التغييرات قبل إنشاء المطعم.',
        savedDraftNotReady: 'احفظ اسم مطعم صحيحًا وعملة صحيحة قبل إنشاء المطعم.',
        createRestaurant: 'إنشاء المطعم',
        creatingRestaurant: 'جارٍ إنشاء المطعم...',
        completionNotReady: 'إعداد المطعم غير جاهز للإكمال. راجع البيانات المحفوظة ثم حاول مرة أخرى.',
        completionTemporaryError: 'إنشاء المطعم غير متاح مؤقتًا. تحقق من حالة الإكمال الحالية قبل إعادة المحاولة.',
        completionGenericError: 'تعذر إكمال إنشاء المطعم. تحقق من الحالة الحالية وأعد المحاولة إذا كان ذلك مسموحًا.',
        completionPendingTitle: 'إنشاء المطعم قيد التنفيذ',
        completionPending: 'لا تزال هناك محاولة لإنشاء المطعم قيد التنفيذ. تحقق من الحالة قبل تنفيذ أي إجراء آخر.',
        checkStatus: 'التحقق من الحالة',
        completionRetryableTitle: 'يمكن إعادة محاولة إنشاء المطعم',
        completionRetryable: 'لم تكتمل محاولة الإنشاء السابقة بنجاح. أكد الخادم أن إعادة المحاولة آمنة.',
        retryCompletion: 'إعادة محاولة إنشاء المطعم',
        completionBlockedTitle: 'إنشاء المطعم يحتاج إلى دعم',
        completionBlocked: 'لا يمكن إعادة محاولة إنشاء المطعم بأمان من هذه الشاشة. تحقق من الحالة أو تواصل مع الدعم.',
        completionReference: 'مرجع الإكمال: {{code}}',
        completedSummaryTitle: 'تم إنشاء المطعم بنجاح',
        completedSummary: 'تم إنشاء المطعم واكتملت جلسة الإعداد الحالية.',
        managerAssignmentDeferred: 'سيتم تعيين مدير المطعم في المرحلة التالية من عملية الإعداد.',
        assignRestaurantManager: 'تعيين مدير للمطعم',
        selectManager: 'اختر المدير',
        assignManager: 'تعيين المدير',
        assigningManager: 'جارٍ تعيين المدير...',
        assignLater: 'التعيين لاحقًا',
        assigned: 'تم التعيين',
        assignedToThisRestaurant: 'مُعيّن لهذا المطعم',
        assignedToAnotherRestaurant: 'مُعيّن لمطعم آخر',
        noManagersAvailable: 'لا يوجد مديرو مطاعم متاحون حاليًا.',
        assignManagerLater: 'يمكنك تعيين مدير لاحقًا.',
        managerAssignmentSuccess: 'تم تعيين مدير المطعم بنجاح.',
        managerAssignmentChanged: 'تغيّر تعيين المدير. أعد التحميل واختر مرة أخرى.',
        managerAssignmentUnavailable: 'تعيين المدير غير متاح مؤقتًا. يرجى المحاولة مرة أخرى.',
        managerAssignmentUnable: 'تعذر تعيين المدير. راجع أحدث حالة للمدير وحاول مرة أخرى.',
        managerAssignmentAuthorizationError: 'لا تسمح جلستك بتعيين مدير. سجّل الدخول مرة أخرى أو تواصل مع المسؤول.',
        managerAssignmentValidationError: 'تعذر التحقق من طلب تعيين المدير.',
        managerAssignmentLoadError: 'تعذر تحميل مديري المطاعم. يرجى المحاولة مرة أخرى.',
        managerAssignmentMissingRestaurant: 'تعذر تعيين مدير لأن هوية المطعم المكتمل غير متاحة.',
        loadingManagers: 'جارٍ تحميل المديرين...',
        whatsappReviewSaved: 'رقم واتساب المحفوظ: {{number}}',
        whatsappReviewNotConnected: 'تم حفظ الرقم للمطعم، لكن واتساب غير متصل أو مهيأ حتى الآن.',
        whatsappReviewMissing: 'لم يتم حفظ رقم واتساب. يمكن إعداد واتساب بعد إنشاء المطعم.'
      }
    }
  },

  ms: {
    translation: {
      dashboard: 'Papan Pemuka',
      orders: 'Pesanan',
      kitchen: 'Dapur',
      customers: 'Pelanggan',
      menu: 'Menu',
      whatsapp: 'WhatsApp',
      reports: 'Laporan',
      settings: 'Tetapan',
      companies: 'Syarikat',
      restaurants: 'Restoran',
      users: 'Pengguna',
      logout: 'Log Keluar',
      ordersToday: 'Pesanan Hari Ini',
      revenueToday: 'Hasil Hari Ini',
      activeCustomers: 'Pelanggan Aktif',
      averageOrderValue: 'Nilai Pesanan Purata',
      sales: 'Jualan',
      salesToday: 'Jualan Hari Ini',
      salesYesterday: 'Jualan Semalam',
      salesLast7Days: 'Jualan (7 Hari Lepas)',
      salesLast30Days: 'Jualan (30 Hari Lepas)',
      salesThisMonth: 'Jualan Bulan Ini',
      ordersYesterday: 'Pesanan Semalam',
      ordersLast7Days: 'Pesanan (7 Hari Lepas)',
      ordersLast30Days: 'Pesanan (30 Hari Lepas)',
      ordersThisMonth: 'Pesanan Bulan Ini',
      welcomeBack: 'Selamat kembali!',
      restaurantPerformanceLine: 'Inilah prestasi restoran anda.',
      salesAnalytics: 'Analitik Jualan',
      orderStatusAnalytics: 'Analitik Status Pesanan',
      newOrders: 'Pesanan Baru',
      preparing: 'Menyediakan',
      ready: 'Sedia',
      completed: 'Selesai',
      searchOrders: 'Cari pesanan...',
      filterByStatus: 'Tapis mengikut status',
      orderNumber: 'Nombor Pesanan',
      customer: 'Pelanggan',
      total: 'Jumlah',
      status: 'Status',
      actions: 'Tindakan',
      viewDetails: 'Lihat Butiran',
      addCustomer: 'Tambah Pelanggan',
      customerName: 'Nama Pelanggan',
      phone: 'Telefon',
      email: 'E-mel',
      totalOrders: 'Jumlah Pesanan',
      totalSpent: 'Jumlah Perbelanjaan',
      addMenuItem: 'Tambah Item Menu',
      itemName: 'Nama Item',
      price: 'Harga',
      category: 'Kategori',
      available: 'Tersedia',
      unavailable: 'Tidak Tersedia',
      dailySales: 'Jualan Harian',
      weeklySales: 'Jualan Mingguan',
      monthlySales: 'Jualan Bulanan',
      topSellingItems: 'Item Terlaris',
      login: 'Log Masuk',
      register: 'Daftar',
      password: 'Kata Laluan',
      name: 'Nama',
      welcome: 'Selamat datang ke UniGate Solution',
      restaurantOnboarding: {
        title: 'Penyediaan restoran',
        loading: 'Memuatkan sesi penyediaan restoran...',
        sessionUnavailable: 'Sesi penyediaan tidak tersedia',
        staleSession: 'Sesi yang disimpan tidak lagi tersedia. Pulihkan penyediaan menggunakan rujukan permintaan sedia ada.',
        staleMissingRequest: 'Penyediaan terdahulu tidak dapat dipulihkan secara automatik kerana rujukan pemulihannya tiada. Hubungi sokongan sebelum bermula semula.',
        recover: 'Pulihkan penyediaan',
        retry: 'Cuba lagi',
        authenticationExpired: 'Sesi anda telah tamat. Sila log masuk semula.',
        permissionDenied: 'Anda tidak mempunyai kebenaran untuk menyediakan restoran.',
        validationError: 'Permintaan penyediaan tidak dapat disahkan.',
        temporaryError: 'Perkhidmatan penyediaan tidak tersedia buat sementara waktu. Sila cuba lagi.',
        genericError: 'Penyediaan restoran tidak dapat dimuatkan. Sila cuba lagi.',
        completedTitle: 'Penyediaan selesai',
        completed: 'Penyediaan restoran ini telah pun selesai.',
        sessionReady: 'Sesi penyediaan sedia',
        foundationReady: 'Sesi penyediaan selamat anda sedia. Lengkapkan butiran restoran di bawah, simpan draf, dan semak maklumat yang disimpan sebelum mencipta restoran.',
        companyConfirmation: 'Pengesahan syarikat',
        company: 'Syarikat',
        subscriptionPlan: 'Pelan langganan: {{plan}}',
        restaurantDetails: 'Butiran restoran',
        draftHelp: 'Simpan draf yang boleh dipulihkan. Ini tidak melengkapkan penyediaan.',
        restaurantName: 'Nama restoran',
        address: 'Alamat',
        phone: 'Telefon',
        whatsappNumber: 'Nombor WhatsApp',
        whatsappReadiness: 'Kesediaan WhatsApp',
        whatsappNumberMissing: 'Tiada nombor WhatsApp disimpan lagi.',
        whatsappNumberSaved: 'Nombor WhatsApp disimpan',
        whatsappConfigurationPending: 'Konfigurasi WhatsApp diperlukan selepas restoran dicipta.',
        whatsappNoProvisioning: 'Menyimpan nombor telefon tidak menyambungkan WhatsApp. Tiada penyediaan dilakukan dalam langkah ini.',
        currency: 'Mata wang (kod 3 huruf)',
        taxEnabled: 'Aktifkan cukai',
        taxRate: 'Kadar cukai (%)',
        saveDraft: 'Simpan draf',
        saving: 'Menyimpan...',
        saved: 'Draf disimpan.',
        saveNotReady: 'Simpan draf tidak tersedia buat masa ini. Muat semula halaman dan cuba lagi.',
        revisionConflict: 'Sesi ini telah berubah. Versi terkini telah dimuatkan; semak sebelum menyimpan lagi.',
        revisionConflictTitle: 'Perubahan lain telah dikesan',
        latestVersionLoaded: 'Versi terkini daripada pelayan telah dimuatkan.',
        previousValuesPreserved: 'Nilai terdahulu anda dikekalkan di sini untuk digunakan semula secara manual jika perlu.',
        restorePreviousValues: 'Pulihkan nilai terdahulu saya',
        dismissConflict: 'Tutup dan semak versi terkini',
        nameTooLong: 'Nama restoran mesti 160 aksara atau kurang.',
        addressTooLong: 'Alamat mesti 500 aksara atau kurang.',
        phoneTooLong: 'Nombor telefon mesti 40 aksara atau kurang.',
        currencyInvalid: 'Mata wang mesti kod tiga huruf seperti USD.',
        taxRateInvalid: 'Kadar cukai mesti antara 0 hingga 100.',
        taxDisabledRate: 'Kadar cukai mesti sifar apabila cukai dimatikan.',
        reviewTitle: 'Semak penyediaan restoran',
        reviewHelp: 'Semak butiran restoran yang disimpan sebelum mencipta restoran.',
        notProvided: 'Tidak diberikan',
        yes: 'Ya',
        no: 'Tidak',
        completionNoticeTitle: 'Sebelum mencipta restoran',
        completionNotice: 'Mencipta restoran akan melengkapkan sesi penyediaan ini. Semak butiran yang disimpan dengan teliti sebelum meneruskan.',
        unsavedChangesBeforeCreate: 'Simpan perubahan anda sebelum mencipta restoran.',
        savedDraftNotReady: 'Simpan nama restoran dan mata wang yang sah sebelum mencipta restoran.',
        createRestaurant: 'Cipta restoran',
        creatingRestaurant: 'Mencipta restoran...',
        completionNotReady: 'Penyediaan restoran belum bersedia untuk dilengkapkan. Semak butiran yang disimpan dan cuba lagi.',
        completionTemporaryError: 'Penciptaan restoran tidak tersedia buat sementara waktu. Semak status semasa sebelum mencuba lagi.',
        completionGenericError: 'Penciptaan restoran tidak dapat diselesaikan. Semak status semasa dan cuba lagi jika dibenarkan.',
        completionPendingTitle: 'Penciptaan restoran sedang berjalan',
        completionPending: 'Percubaan mencipta restoran masih sedang berjalan. Semak status sebelum mengambil tindakan lain.',
        checkStatus: 'Semak status',
        completionRetryableTitle: 'Penciptaan restoran boleh dicuba semula',
        completionRetryable: 'Percubaan sebelumnya tidak selesai dengan berjaya. Pelayan telah mengesahkan bahawa percubaan semula adalah selamat.',
        retryCompletion: 'Cuba cipta restoran semula',
        completionBlockedTitle: 'Penciptaan restoran memerlukan sokongan',
        completionBlocked: 'Restoran tidak boleh dicuba semula dengan selamat dari skrin ini. Semak status atau hubungi sokongan.',
        completionReference: 'Rujukan penyelesaian: {{code}}',
        completedSummaryTitle: 'Restoran berjaya dicipta',
        completedSummary: 'Restoran telah dicipta dan sesi penyediaan ini telah selesai.',
        managerAssignmentDeferred: 'Penetapan pengurus restoran akan dibuat pada peringkat penyediaan seterusnya.',
        assignRestaurantManager: 'Tetapkan pengurus restoran',
        selectManager: 'Pilih pengurus',
        assignManager: 'Tetapkan pengurus',
        assigningManager: 'Menetapkan pengurus...',
        assignLater: 'Tetapkan kemudian',
        assigned: 'Telah ditetapkan',
        assignedToThisRestaurant: 'Ditetapkan kepada restoran ini',
        assignedToAnotherRestaurant: 'Ditetapkan kepada restoran lain',
        noManagersAvailable: 'Tiada pengurus restoran tersedia buat masa ini.',
        assignManagerLater: 'Anda boleh menetapkan pengurus kemudian.',
        managerAssignmentSuccess: 'Pengurus restoran berjaya ditetapkan.',
        managerAssignmentChanged: 'Penetapan pengurus telah berubah. Muat semula dan pilih sekali lagi.',
        managerAssignmentUnavailable: 'Penetapan pengurus tidak tersedia buat sementara waktu. Sila cuba lagi.',
        managerAssignmentUnable: 'Pengurus tidak dapat ditetapkan. Semak status terkini pengurus dan cuba lagi.',
        managerAssignmentAuthorizationError: 'Sesi anda tidak membenarkan penetapan pengurus. Log masuk semula atau hubungi pentadbir.',
        managerAssignmentValidationError: 'Penetapan pengurus tidak dapat disahkan.',
        managerAssignmentLoadError: 'Pengurus restoran tidak dapat dimuatkan. Sila cuba lagi.',
        managerAssignmentMissingRestaurant: 'Pengurus tidak dapat ditetapkan kerana identiti restoran yang telah siap tidak tersedia.',
        loadingManagers: 'Memuatkan pengurus...',
        whatsappReviewSaved: 'Nombor WhatsApp disimpan: {{number}}',
        whatsappReviewNotConnected: 'Nombor ini disimpan untuk restoran tetapi WhatsApp belum disambungkan atau disediakan.',
        whatsappReviewMissing: 'Tiada nombor WhatsApp disimpan. WhatsApp boleh dikonfigurasikan selepas restoran dicipta.'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
